import { getToken, removeToken, setToken } from '@/stores/auth'
import { API_BASE_URL } from '@/lib/constants'

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

type RequestOptions = Omit<RequestInit, 'body'> & {
  body?: unknown
}

// 同時に複数の 401 が来ても 1 回だけリフレッシュする
let refreshPromise: Promise<boolean> | null = null

async function tryRefresh(): Promise<boolean> {
  if (refreshPromise) return refreshPromise

  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      if (!res.ok) return false
      const data = await res.json() as { token: string }
      setToken(data.token)
      return true
    } catch {
      return false
    }
  })().finally(() => {
    refreshPromise = null
  })

  return refreshPromise
}

export async function apiRequest<T>(
  path: string,
  options: RequestOptions = {},
  _isRetry = false,
): Promise<T> {
  const token = getToken()
  const hasBody = options.body !== undefined

  const headers: Record<string, string> = {
    ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
    ...(options.headers as Record<string, string>),
  }

  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const res = await fetch(`${API_BASE_URL}${path}`, {
    ...options,
    headers,
    credentials: 'include',
    body: hasBody ? JSON.stringify(options.body) : undefined,
  })

  if (!res.ok) {
    let body: unknown
    try {
      body = await res.json()
    } catch {
      body = null
    }
    const b = body as Record<string, unknown>
    const message =
      typeof b?.error === 'object' && b.error !== null && 'message' in (b.error as object)
        ? String((b.error as Record<string, unknown>).message)
        : typeof b?.message === 'string'
          ? b.message
          : res.statusText

    // JWT 期限切れ → リフレッシュトークンで更新してリトライ
    if (res.status === 401 && token && !_isRetry) {
      const refreshed = await tryRefresh()
      if (refreshed) {
        return apiRequest<T>(path, options, true)
      }
      // リフレッシュも失敗 → ログアウト
      removeToken()
      window.location.href = '/login'
    }

    throw new ApiError(res.status, message, body)
  }

  if (res.status === 204) {
    return undefined as T
  }

  return res.json() as Promise<T>
}
